import styled from '@emotion/styled';
import { FileDownloadRounded, PlaylistAdd } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { spacing } from '@mui/system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { MasterlistCheckState } from '../../../../hooks/api/useMasterlistCheck';
import { useTrackUserEvent } from '../../../../hooks/api/useTrackUserEvent';
import { Journals } from '../../../../types/Journals.types';
import { MasterlistCheckCounts } from '../../../../types/masterlistCheck.types';
import { AlertMessage } from '../../../../components/AlertMessage';
import { JournalModal } from './JournalModal';
import { JournalsTableDesktop } from './JournalsTableDesktop';
import { JournalsTableMobile } from './JournalsTableMobile';
import { checkFor, JournalFilter, STATUS_FILTERS } from './masterlistCheck';
import { isMissingColumn } from './utils';

const StyledGrid = styled(Grid)(spacing);
const StyledTypography = styled(Typography)<{ component: string }>(spacing);

export function JournalsTable(props: {
  /** The rows shown: after the search and the "Show" filter (both in JournalsContainer). */
  journals: Journals[];
  /** Before the search and the "Show" filter: the missing-data alert and ?journal= use these. */
  allJournals: Journals[];
  filter: JournalFilter;
  onFilterChange: (filter: JournalFilter) => void;
  /** A summary count was clicked: show all its journals. */
  onSummaryFilter: (filter: JournalFilter) => void;
  masterlist: MasterlistCheckState;
  preview: boolean;
  onExport?: () => void;
  agreementName?: string;
}) {
  const { account, impersonationLock } = useAuthContext();
  const trackUserEvent = useTrackUserEvent();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [journal, setJournal] = useState<Journals>();
  const { filter, onFilterChange: setFilter, journals } = props;
  const { agreementId } = useParams<{ agreementId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const journalParam = searchParams.get('journal');
  const source = searchParams.get('source') ?? undefined;

  const {
    masterlistCheckEnabled: checkEnabled,
    masterlistCheck,
    checksByJournalId,
    isMasterlistCheckLoading,
    masterlistCheckError,
  } = props.masterlist;
  const showMasterlist = !!masterlistCheck;

  useEffect(() => {
    if (!journalParam || !props.allJournals.length) return;
    const found = props.allJournals.find((j) => j._id === journalParam);
    if (found) {
      setJournal(found);
      setOpen(true);
      window.scrollTo({ top: 0 });
    }
  }, [journalParam, props.allJournals]);

  // Over the unfiltered list, so the alert doesn't change with the search or the filter.
  const allJournals = props.allJournals;
  const someMissingPublishingModel = allJournals?.some((journal) =>
    isMissingColumn(journal, 'publishing_model')
  );
  const someMissingPayment = allJournals?.some((journal) =>
    isMissingColumn(journal, 'payment')
  );
  const someMissingLicenses = allJournals?.some((journal) =>
    isMissingColumn(journal, 'licenses')
  );
  const someMissingName = allJournals?.some((journal) =>
    isMissingColumn(journal, 'name')
  );
  const someMissingUrl = allJournals?.some((journal) =>
    isMissingColumn(journal, 'url')
  );
  const missingLabel = useMemo(() => {
    const missing = [];
    if (someMissingPublishingModel) {
      missing.push('publishing model');
    }
    if (someMissingPayment) {
      missing.push('payment');
    }
    if (someMissingLicenses) {
      missing.push('licenses');
    }
    if (someMissingName) {
      missing.push('name');
    }
    if (someMissingUrl) {
      missing.push('url');
    }
    if (missing.length === 0) {
      return '';
    }
    const joined =
      missing.length > 2 ? missing.join(', ') : missing.join(' and ');
    return (
      <>
        <AlertMessage severity="warning" title="Missing mandatory data">
          Some journals are missing the following mandatory data:{' '}
          <strong>{joined}</strong>
        </AlertMessage>
      </>
    );
  }, [
    someMissingPublishingModel,
    someMissingPayment,
    someMissingLicenses,
    someMissingName,
    someMissingUrl,
  ]);

  const filterOptions: { value: JournalFilter; label: string }[] = [
    { value: 'all', label: t('agreements.journalFilter.all') },
    { value: 'missing_data', label: t('agreements.journalFilter.missingData') },
    // Only with a check to filter by: a failed or reloading check would empty the table.
    ...(showMasterlist
      ? STATUS_FILTERS.map(({ filter, labelKey }) => ({
          value: filter,
          label: t(labelKey),
        }))
      : []),
  ];

  const handleAddNew = () => {
    setJournal(undefined);
    setOpen(true);
  };

  // Stable, so the desktop table's memoized Virtuoso context holds across renders.
  const handleOpen = useCallback((journal: Journals) => {
    setJournal(journal);
    setOpen(true);
  }, []);

  const handleClose = () => {
    setOpen(false);
    if (journalParam) setSearchParams({});
  };

  return (
    <div>
      <StyledGrid container spacing={6}>
        <Grid item xs={12} sm={12} lg={12} xl={12}>
          <Grid
            container
            sx={{
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: { xs: 'flex-start', md: 'space-between' },
              mb: 12,
            }}
          >
            <Grid item xs={9}>
              <StyledTypography
                component="p"
                variant="h3"
                sx={{ fontSize: '18px' }}
                gutterBottom
              >
                {props.preview
                  ? t('agreements.journalTable.previewLabel')
                  : t('agreements.uploadJournal')}
              </StyledTypography>
              {missingLabel && (
                <Box sx={{ mt: 18, mb: 14 }}>{missingLabel}</Box>
              )}
              {checkEnabled && (
                // Same width as the search field above the table.
                <Box sx={{ mt: 18, mb: 14, maxWidth: { xs: '100%', md: 500 } }}>
                  <MasterlistSummary
                    counts={masterlistCheck?.counts}
                    loading={isMasterlistCheckLoading}
                    failed={!!masterlistCheckError && !masterlistCheck}
                    onFilter={props.onSummaryFilter}
                  />
                </Box>
              )}
              <FormControl size="small" sx={{ mt: 12, minWidth: 240 }}>
                <InputLabel id="journal-filter-label">
                  {t('agreements.journalFilter.label')}
                </InputLabel>
                <Select<JournalFilter>
                  labelId="journal-filter-label"
                  id="journal-filter"
                  value={filter}
                  label={t('agreements.journalFilter.label')}
                >
                  {filterOptions.map((option) => (
                    // On the item, not the Select's onChange: that doesn't fire for the shown
                    // value, which can be "all" standing in for a status choice while the check
                    // loads. Picking it then has to replace that choice.
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      onClick={() => setFilter(option.value)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!props.preview && (
              <>
                <Grid item>
                  <div className="flex gap-2">
                    {props.onExport && (
                      <Button
                        variant="secondary"
                        endIcon={<FileDownloadRounded />}
                        onClick={() => {
                          trackUserEvent.mutate({
                            slug: account,
                            eventCode: 'c',
                            payload: {
                              on: 'export_journals',
                              agreementId,
                            },
                          });
                          props.onExport?.();
                        }}
                        disabled={impersonationLock}
                      >
                        Export
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      endIcon={<PlaylistAdd />}
                      onClick={handleAddNew}
                      disabled={impersonationLock}
                    >
                      Add Journal
                    </Button>
                  </div>
                </Grid>
              </>
            )}
          </Grid>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <JournalsTableDesktop
              journals={journals}
              onJournalClick={handleOpen}
              preview={props.preview}
              checksByJournalId={showMasterlist ? checksByJournalId : undefined}
            />
          </Box>
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <JournalsTableMobile
              journals={journals}
              onJournalClick={handleOpen}
              checksByJournalId={showMasterlist ? checksByJournalId : undefined}
            />
          </Box>
          <JournalModal
            journal={journal}
            open={open}
            handleClose={handleClose}
            editMode={!!journal}
            agreementName={props.agreementName ?? ''}
            source={source}
            masterlistCheck={checkFor(checksByJournalId, journal)}
          />
        </Grid>
      </StyledGrid>
    </div>
  );
}

const SUMMARY_SX = {
  px: 24,
  py: 16,
  // The message area scrolls by default, which clips the links' focus ring.
  '& .MuiAlert-message': { overflow: 'visible' },
};

function MasterlistSummary({
  counts,
  loading,
  failed,
  onFilter,
}: {
  counts?: MasterlistCheckCounts;
  loading: boolean;
  failed: boolean;
  onFilter: (filter: JournalFilter) => void;
}) {
  const { t } = useTranslation();
  const title = t('agreements.masterlistCheck.summaryTitle');

  if (loading) {
    return (
      <AlertMessage severity="info" role="status" title={title} sx={SUMMARY_SX}>
        {t('agreements.masterlistCheck.loading')}
      </AlertMessage>
    );
  }
  if (failed || !counts) {
    return (
      <AlertMessage severity="info" role="status" title={title} sx={SUMMARY_SX}>
        {t('agreements.masterlistCheck.failed')}
      </AlertMessage>
    );
  }

  const visibleParts = STATUS_FILTERS.filter(
    ({ filter }) => counts[filter] > 0
  );

  return (
    <AlertMessage
      // Green only when nothing needs a look: a ceased match is still a warning.
      severity={
        counts.total > 0 &&
        counts.matches === counts.total &&
        counts.ceased === 0
          ? 'success'
          : 'info'
      }
      // Not a live region, unlike loading and failed: a refetch would read out every link.
      // Alert defaults to role="alert".
      role="none"
      title={title}
      sx={SUMMARY_SX}
    >
      <Box component="p" sx={{ m: 0 }}>
        {t('agreements.masterlistCheck.summary.matches', {
          matches: counts.matches,
          total: counts.total,
        })}
      </Box>
      {visibleParts.length > 0 && (
        <Box component="p" sx={{ m: 0, mt: 4 }}>
          {visibleParts.map((part, i) => (
            <span key={part.filter}>
              {i > 0 && (
                // Room for the links' focus rings on either side.
                <Box component="span" aria-hidden="true" sx={{ mx: 8 }}>
                  ·
                </Box>
              )}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => onFilter(part.filter)}
                // Closer than the theme's 4px, so the ring stays clear of the "·".
                sx={{
                  verticalAlign: 'baseline',
                  '&:focus-visible': { outlineOffset: '2px' },
                }}
              >
                {t(`agreements.masterlistCheck.summary.${part.summaryKey}`, {
                  count: counts[part.filter],
                })}
                <span className="sr-only">
                  {t('agreements.masterlistCheck.summary.showOnly')}
                </span>
              </Link>
            </span>
          ))}
        </Box>
      )}
    </AlertMessage>
  );
}
